import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MessageCircle, X, Users } from 'lucide-react';

/**
 * Chat notification toast manager.
 * Renders floating notification toasts when messages arrive.
 * Each toast shows avatar, sender name, message preview.
 * Click opens the chat.
 */

// Singleton event bus for chat notifications
const listeners = new Set();
let toastIdCounter = 0;

export const ChatNotificationManager = {
    /**
     * Show a chat message notification.
     * @param {{ senderName, message, chatId, chatName, photo, isGroup, targetUser }} data
     */
    notify: (data) => {
        const id = ++toastIdCounter;
        const toast = { id, ...data, createdAt: Date.now() };
        listeners.forEach(fn => fn(toast));
    }
};

// The visual toast component
const ChatToast = ({ toast, onDismiss, onOpen }) => {
    const [exiting, setExiting] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => {
        timerRef.current = setTimeout(() => {
            setExiting(true);
            setTimeout(() => onDismiss(toast.id), 400);
        }, 5000);
        return () => clearTimeout(timerRef.current);
    }, [toast.id, onDismiss]);

    const handleClick = () => {
        clearTimeout(timerRef.current);
        onOpen(toast);
        setExiting(true);
        setTimeout(() => onDismiss(toast.id), 200);
    };

    const handleClose = (e) => {
        e.stopPropagation();
        clearTimeout(timerRef.current);
        setExiting(true);
        setTimeout(() => onDismiss(toast.id), 200);
    };

    const displayName = toast.senderName || 'Someone';
    const isGroup = toast.isGroup;
    const chatLabel = isGroup ? (toast.chatName || 'Group') : displayName;
    const messagePreview = (toast.message || '').length > 60
        ? toast.message.substring(0, 60) + '…'
        : (toast.message || 'Sent a message');

    return (
        <div
            onClick={handleClick}
            className={`
                flex items-center gap-3 p-3 mb-2 rounded-2xl cursor-pointer
                bg-white/95 backdrop-blur-xl border border-slate-200/60
                shadow-[0_8px_30px_rgba(0,0,0,0.12)]
                hover:shadow-[0_8px_30px_rgba(0,0,0,0.18)]
                hover:bg-white transition-all duration-300
                ${exiting ? 'animate-toast-exit' : 'animate-toast-enter'}
            `}
            style={{ minWidth: '320px', maxWidth: '400px' }}
        >
            {/* Avatar */}
            <div className="relative shrink-0">
                {toast.photo ? (
                    <img
                        src={toast.photo}
                        alt=""
                        className="w-11 h-11 rounded-full object-cover border-2 border-indigo-100"
                    />
                ) : (
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white font-bold text-sm border-2 border-indigo-100">
                        {displayName.substring(0, 2).toUpperCase()}
                    </div>
                )}
                {isGroup && (
                    <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-indigo-600 rounded-full border-2 border-white flex items-center justify-center">
                        <Users size={9} className="text-white" />
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                    <span className="font-bold text-sm text-slate-800 truncate">
                        {chatLabel}
                    </span>
                    <span className="text-[10px] text-indigo-500 font-medium bg-indigo-50 px-1.5 py-0.5 rounded-full shrink-0">
                        New
                    </span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5 truncate leading-relaxed">
                    {isGroup && toast.senderName && (
                        <span className="font-semibold text-slate-600">{toast.senderName}: </span>
                    )}
                    {messagePreview}
                </p>
            </div>

            {/* Close */}
            <button
                onClick={handleClose}
                className="p-1 text-slate-300 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors shrink-0"
            >
                <X size={14} />
            </button>
        </div>
    );
};

/**
 * ChatNotificationProvider — renders toast notifications above the minimised chat
 * bubbles in the bottom-right corner, so new messages appear where the conversations
 * themselves live rather than over the page header.
 * Must be placed once in the app tree (e.g., in DashboardLayout).
 */
export const ChatNotificationProvider = ({ onOpenChat }) => {
    const [toasts, setToasts] = useState([]);

    // Subscribe to notification events
    useEffect(() => {
        const handler = (toast) => {
            setToasts(prev => {
                // Max 4 visible at once
                const updated = [toast, ...prev].slice(0, 4);
                return updated;
            });

            // Play notification sound
            try {
                const audioContext = new (window.AudioContext || window.webkitAudioContext)();
                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();
                oscillator.connect(gainNode);
                gainNode.connect(audioContext.destination);
                oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
                oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
                gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
                gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.3);
                oscillator.start(audioContext.currentTime);
                oscillator.stop(audioContext.currentTime + 0.3);
            } catch (e) { /* audio not available */ }
        };

        listeners.add(handler);
        return () => listeners.delete(handler);
    }, []);

    const dismiss = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const handleOpen = useCallback((toast) => {
        if (onOpenChat) {
            onOpenChat(toast);
        }
    }, [onOpenChat]);

    if (toasts.length === 0) return null;

    return createPortal(
        <div
            // Bottom-right, clear of the 56px bubble column plus its 16px margin, so a
            // toast sits ABOVE the bubbles instead of covering them. `flex-col-reverse`
            // grows the stack upwards, away from the corner.
            className="fixed z-[9999] flex flex-col-reverse items-end gap-2"
            style={{ pointerEvents: 'none', right: 16, bottom: 16 + 56 + 12 }}
        >
            {toasts.map(toast => (
                <div key={toast.id} style={{ pointerEvents: 'auto' }}>
                    <ChatToast
                        toast={toast}
                        onDismiss={dismiss}
                        onOpen={handleOpen}
                    />
                </div>
            ))}
        </div>,
        document.body
    );
};

export default ChatNotificationProvider;
